import { useState } from "react";
import { LuUpload } from "react-icons/lu";
import { Small } from "@/components/typography";
import { Button } from "@/components/ui/button";
import { manualUploadPrompt } from "@/lib/page-status";
import { useQueue } from "@/lib/queue-store";
import { cn } from "@/lib/utils";
import type { Page } from "@/lib/page";

/** The scanner's "go now" for one script: clears backoff, lifts a global
 * pause, and drains the queue immediately. The queue gets there on its own
 * eventually — but "eventually" is up to five minutes of backoff, or however
 * long a paused queue waits for its 5s tick, and the person standing at the
 * table with the next script in hand has no way to tell a stalled upload
 * from a slow one. Rendered per script (Home, and the script screen) rather
 * than once globally, so "did this one go?" has an answer next to that one.
 *
 * Absent when there is nothing to send — see `manualUploadPrompt`. */
export function UploadNowButton({ pages, className }: { pages: Page[]; className?: string }) {
  const queue = useQueue();
  const [sending, setSending] = useState(false);
  const prompt = manualUploadPrompt(pages);
  if (!prompt) return null;

  async function send(targets: Page[]) {
    setSending(true);
    try {
      await queue?.retryNow(targets);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={cn("flex items-center justify-between gap-2 rounded-md border px-3 py-2", className)}>
      <Small>{prompt.message}</Small>
      <Button
        size="sm"
        variant="outline"
        disabled={!queue || sending}
        onClick={() => void send(prompt.pages)}
      >
        <LuUpload />
        {sending ? "Sending…" : prompt.action}
      </Button>
    </div>
  );
}
